import type { ProjectWithContext } from '@/shared/types/domain';

/**
 * O que estas leituras precisam de um projeto: a situação e o relógio, nada
 * mais. Tipo estreito de propósito — a vizinhança da data (§6.5) traz uma
 * projeção reduzida, não um `ProjectWithContext` inteiro, e o crachá dela
 * precisa da mesma verdade que o da tabela.
 */
export type ProjectClockView = Pick<ProjectWithContext, 'status' | 'starts_at' | 'ends_at'>;

/**
 * O relógio da RODADA de ajustes, que é outro relógio: não o do evento, o das
 * duas marcas que a devolução e a edição deixam no projeto.
 */
export type ProjectAdjustmentView = Pick<
  ProjectWithContext,
  'status' | 'decided_at' | 'updated_at'
>;

/**
 * As leituras DERIVADAS de um projeto: o que a situação da esteira mais o
 * relógio dizem sobre ele. `isRunningNow` e `isAwaitingClosure` substituíram o
 * estado `in_progress` removido da esteira (spec §1, decisão 3);
 * `needsImmediateAction` é o predicado de urgência da listagem.
 *
 * Elas moram na feature de projetos de propósito, e não em `constants/statuses.ts`:
 * não são situações da esteira, são leituras do relógio sobre uma situação. Um
 * valor de enum que ninguém pode escrever seria armadilha esperando alguém
 * escrever nela; uma expressão não tem esse problema.
 */

/** `approved` e a data de hoje dentro da janela do evento. */
export function isRunningNow(project: ProjectClockView, now: Date = new Date()): boolean {
  if (project.status !== 'approved' || !project.starts_at || !project.ends_at) return false;
  const instant = now.getTime();
  return Date.parse(project.starts_at) <= instant && instant <= Date.parse(project.ends_at);
}

/**
 * `approved` e a janela já passou: o evento aconteceu e ninguém o encerrou.
 * É o que vai alimentar o contador de pendências quando o relatório
 * pós-projeto existir (v1.1).
 */
export function isAwaitingClosure(project: ProjectClockView, now: Date = new Date()): boolean {
  if (project.status !== 'approved' || !project.ends_at) return false;
  return Date.parse(project.ends_at) < now.getTime();
}

/**
 * O líder já mexeu no projeto devolvido, e ainda não reenviou (decisão 43).
 *
 * É o que separa "Ajustar projeto" de "Continuar ajustes" — o rótulo que diz em
 * que ponto da rodada a pessoa está. **Derivado, nunca coluna nova**, e a
 * derivação é exata (não aproximada) por três propriedades do banco:
 *
 * 1. `decide_project` grava `decided_at = now()` no MESMO `UPDATE` em que o
 *    trigger grava `updated_at = now()`. Dentro de uma transação `now()` é
 *    constante, então logo após a devolução os dois valores são **idênticos** —
 *    e o `>` estrito lê corretamente "ninguém mexeu".
 * 2. `save_project_draft` sempre incrementa `version` e move `updated_at`,
 *    qualquer que seja o patch. Mexer só na lista de apoio, ou só nos espaços,
 *    também conta. Não há alteração invisível a este predicado.
 * 3. Os dois campos já viajam na `projects_with_context`, que a tela carrega —
 *    zero consulta a mais e zero flicker. Ler a linha do tempo para isto
 *    exigiria uma segunda query, e o rótulo trocaria na cara do usuário.
 *
 * Mora aqui, junto de `isRunningNow` e `isAwaitingClosure`, pela mesma razão que
 * elas: é leitura do relógio sobre uma situação, não uma situação nova.
 */
export function hasUnsentAdjustments(project: ProjectAdjustmentView): boolean {
  if (project.status !== 'changes_requested') return false;
  // Sem devolução não há rodada de ajuste para estar no meio — e sem o marco,
  // qualquer comparação com `updated_at` daria verdadeiro por acidente.
  if (!project.decided_at) return false;
  return Date.parse(project.updated_at) > Date.parse(project.decided_at);
}

/**
 * "Alguém precisa fazer alguma coisa com este projeto agora?"
 *
 * Os três caminhos respondem à mesma pergunta — "alguém tem de FAZER alguma
 * coisa que ninguém resolve olhando a lista?":
 *
 * 1. `changes_requested`: a bola voltou para o ministério e só anda se mexerem.
 * 2. `approved` com o evento encerrado: aconteceu e ninguém prestou contas.
 * 3. Data de execução já vencida sem decisão: o caso degenerado — o projeto não
 *    é terminal e o mês dele já passou, então é o que mais depende de alguém
 *    reparar nele.
 *
 * ⚠️ **Atraso de decisão NÃO entra**, e a ausência é deliberada. Um projeto
 * parado há 20 dias cujo evento é em outubro precisa de um empurrão, não de
 * resgate: ele continua planejável, e marcá-lo faria a cor deixar de significar
 * "sente e resolva". A linha dele segue dizendo "aguardando decisão há 20 dias"
 * na coluna de espera, em preto, no mês em que ele acontece.
 *
 * `switch` exaustivo e **sem `default`**: situação nova tem de quebrar o build
 * em vez de silenciosamente deixar de pedir ação.
 */
export function needsImmediateAction(
  project: ProjectClockView,
  now: Date = new Date(),
): boolean {
  switch (project.status) {
    case 'draft':
    case 'changes_requested':
      return true;

    case 'approved':
      return isAwaitingClosure(project, now);

    case 'under_review':
      return hasPassed(project, now);

    case 'rejected':
    case 'cancelled':
    case 'completed':
      return false;
  }
}

/**
 * Quantos da lista pedem ação. É o número âmbar do cabeçalho de `/projetos` —
 * o aviso que independe de onde a tabela está rolada, e o que impede o
 * `changes_requested` de evento distante de passar despercebido lá embaixo, no
 * mês dele.
 */
export function countNeedingAction(
  projects: readonly ProjectClockView[],
  now: Date = new Date(),
): number {
  return projects.filter((project) => needsImmediateAction(project, now)).length;
}

/** O evento já terminou. Sem data, nada venceu — o projeto ainda vai marcar uma. */
function hasPassed(project: ProjectClockView, now: Date): boolean {
  const end = project.ends_at ?? project.starts_at;
  return end ? Date.parse(end) < now.getTime() : false;
}
